import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import type { SessionRow } from './sessions';

export type ProjectRow = {
  id: string;
  user_id: number;
  name: string;
  description: string;
  system_prompt: string;
  created_at: number;
  updated_at: number;
};

export const projectKeys = {
  all: ['projects'] as const,
  user: (userId: number) => ['projects', userId] as const,
  one: (id: string, userId: number) => ['projects', userId, id] as const,
};

export function useProjects() {
  const { user } = useAuth();
  return useQuery({
    queryKey: user ? projectKeys.user(user.id) : ['projects', 'nil'],
    enabled: user != null,
    queryFn: async () => {
      const r = await api<{ projects: ProjectRow[] }>('/projects');
      return r.projects;
    },
  });
}

export function useProject(id: string | null | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: id && user ? projectKeys.one(id, user.id) : ['projects', 'nil'],
    enabled: !!id && user != null,
    queryFn: async () => {
      const r = await api<{ project: ProjectRow; sessions: SessionRow[] }>(`/projects/${id}`);
      return r;
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string; system_prompt?: string }) => {
      const r = await api<{ project: ProjectRow }>('/projects', { method: 'POST', body });
      return r.project;
    },
    onSuccess: () => {
      if (user) void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
    },
  });
}

export function useUpdateProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: string;
      patch: Partial<Pick<ProjectRow, 'name' | 'description' | 'system_prompt'>>;
    }) => {
      const r = await api<{ project: ProjectRow }>(`/projects/${id}`, {
        method: 'PATCH',
        body: patch,
      });
      return r.project;
    },
    onSuccess: () => {
      if (user) void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      await api(`/projects/${id}`, { method: 'DELETE' });
    },
    onSuccess: (_data, id) => {
      if (!user) return;
      qc.removeQueries({ queryKey: projectKeys.one(id, user.id), exact: true });
      void qc.invalidateQueries({ queryKey: projectKeys.user(user.id) });
    },
  });
}
